import { Stack } from '@chakra-ui/react';
import AppRoutes from 'config/AppRoutes';
import { FlowChoiceType } from 'features/utils/types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateFlowChoice } from 'store/answers/answersSlice';
import { StepParamsSelector } from 'store/selectors';
import PageLayout from 'ui/layout/PageLayout';
import theme from 'ui/theme/theme';
import useAsset, { AssetType } from 'utils/hooks/useAsset';
import { useEffect, useState } from 'react';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import ChoiceContainer from './components/ChoiceContainer';
import useAmountSteps from './hooks/useAmountSteps';

const FlowChoice = () => {
  const currentRoute = AppRoutes.FLOW_CHOICE;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const simpleVideoUrl = useAsset(
    AssetType.VIDEO,
    `/-1-flowChoice/HOUSE_B_600.mp4`
  );
  const complexVideoUrl = useAsset(
    AssetType.VIDEO,
    `/-1-flowChoice/HOUSE_A_600.mp4`
  );
  const stepParams = useSelector(StepParamsSelector);
  const { navigateToNextStep } = useStepNavigation(currentRoute);
  const [shouldGoToNextStep, setShouldGoToNextStep] = useState(false);
  const { amountStepsSimple, amountStepsComplex } =
    useAmountSteps(currentRoute);

  const handleChoice = (choice: FlowChoiceType) => {
    dispatch(updateFlowChoice(choice));
    setShouldGoToNextStep(true);
  };

  // NOTE: using an effect to navigate to the next step so that the state is updated with the chosen flow
  useEffect(() => {
    if (shouldGoToNextStep) {
      navigateToNextStep();
    }
  }, [shouldGoToNextStep, stepParams, currentRoute, navigateToNextStep]);

  return (
    <PageLayout
      title={t('FLOW_CHOICE_title')}
      titleStyle={{
        fontSize: { base: '24px', md: '36px' },
        position: { xl: 'relative' },
        top: { xl: '-50px' },
      }}
      layoutStyle={{
        background: theme.colors.reno.grey500,
        width: '90vw',
        justifyContent: 'center',
      }}
    >
      <Stack
        h={{ base: 'full', md: 'auto' }}
        pt="0px"
        w={{ base: 'full', md: 'auto' }}
        gap={{ base: '10px', md: '25px' }}
        justifyContent="center"
        alignItems="center"
        direction={{ base: 'column', xl: 'row' }}
      >
        <ChoiceContainer
          title={t('FLOW_CHOICE_simple_title')}
          subtitle={t('FLOW_CHOICE_simple_subtitle')}
          videoUrl={simpleVideoUrl}
          descriptionList={[
            {
              key: 'FLOW_CHOICE_simple_description_1',
              values: {
                amount_steps: amountStepsSimple.toString(),
              },
            },
            'FLOW_CHOICE_simple_description_2',
            // 'FLOW_CHOICE_simple_description_3',
          ]}
          onContinue={() => handleChoice(FlowChoiceType.SIMPLE)}
          animIndex={0}
        />
        <ChoiceContainer
          title={t('FLOW_CHOICE_complex_title')}
          subtitle={t('FLOW_CHOICE_complex_subtitle')}
          videoUrl={complexVideoUrl}
          descriptionList={[
            {
              key: 'FLOW_CHOICE_complex_description_1',
              values: {
                amount_steps: amountStepsComplex.toString(),
              },
            },
            'FLOW_CHOICE_complex_description_2',
          ]}
          onContinue={() => handleChoice(FlowChoiceType.COMPLEX)}
          animIndex={1}
        />
      </Stack>
    </PageLayout>
  );
};

export default FlowChoice;
